import { useEffect } from "react";

import { Button } from "@newsela/angelou";

import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { IStandardsSetListViewModel } from "@/search/NewSearchPageMVP/types/IStandardsSetListViewModel";
import { useStandardsModalContext } from "@/search/NewSearchPageMVP/provider/StandardsModalProvider";
import {
  subtitleCss,
  titleCss,
} from "@/search/NewSearchPageMVP/FilterPanel/filters/Standards/StandardsModal/shared/StandardsSetsHeader/StandardsSetsHeader.styles";
import { STANDARDS_SET_MODE } from "@/search/NewSearchPageMVP/types/IStandardsSetMode";
import { StandardsList } from "../../shared/StandardsList/StandardsList.component";
import { StandardsModalCheckbox } from "../../shared/StandardsModalCheckbox";
import { StandardsSetsHeader } from "../../shared/StandardsSetsHeader";
import { StandardsSetsTitleInput } from "../../shared/StandardsSetsTitleInput";
import { SelectedStandardsInSet } from "../../shared/SelectedStandardsInSet";

import {
  editableHeaderCss,
  inputAndFiltersCss,
  editButtonCss,
  listCss,
  filtersCss,
  headerContainerCss,
  titleAndSubtitleContainer,
  titleInputContainerCss,
  selectedChipsCss,
} from "./EditableStandardsSet.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface EditableStandardsSetProps {
  standardsSet: IStandardsSetListViewModel;
}

export const EditableStandardsSet = ({
  standardsSet,
}: EditableStandardsSetProps) => {
  const {
    getters: { isEditingSet, standardsSetMode },
    handlers: { setStandardsSetMode, setIsEditingSet },
  } = useStandardsModalContext();

  const isEditMode = standardsSetMode === STANDARDS_SET_MODE.EDIT;

  const toggleEditMode = () => {
    setStandardsSetMode(STANDARDS_SET_MODE.EDIT);
    setIsEditingSet(true);
  };

  useEffect(() => {
    if (isEditingSet) setStandardsSetMode("edit");

    if (!isEditingSet) setStandardsSetMode("view");
  }, [isEditingSet]);

  if (!standardsSet.children) return null;

  return (
    <>
      {!isEditMode ? (
        <div css={editableHeaderCss}>
          <div css={titleAndSubtitleContainer}>
            {<p css={titleCss}>{standardsSet.title}</p>}
            <span css={subtitleCss}>
              {
                <span>
                  Select standards to find results that fit your needs
                </span>
              }
            </span>
          </div>
          <ButtonWithTrackEvent
            size={Button.sizes.lg}
            flavor={Button.flavors.secondary}
            onClick={toggleEditMode}
            __cssFor={editButtonCss}
            objectType={objectTypes.content}
            actionName={actionNames.update}
            streamProperties={{
              description: "Edit standard set",
            }}
          >
            Edit set
          </ButtonWithTrackEvent>
        </div>
      ) : null}
      {isEditMode ? (
        <>
          <div css={headerContainerCss}>
            <div css={inputAndFiltersCss}>
              <div css={titleInputContainerCss}>
                <StandardsSetsTitleInput />
              </div>
              <div css={filtersCss}>
                <StandardsSetsHeader />
              </div>
            </div>
          </div>
          <div css={selectedChipsCss}>
            {!!standardsSet.children.length && <SelectedStandardsInSet />}
          </div>
          <StandardsList />
        </>
      ) : (
        <ul css={listCss} data-testid="standards-list">
          {standardsSet.children.map((standard) => {
            return (
              <StandardsModalCheckbox
                key={standard.content_id}
                standard={standard}
              />
            );
          })}
        </ul>
      )}
    </>
  );
};
